import React, { useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Card,
  Divider,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconRobot,
  IconCopy,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";

const parseInsightToBullets = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.filter(Boolean).map((x) => String(x).trim()).filter(Boolean);
  }
  const value = String(raw).trim();

  // Normalize common bullet formats to line‑based items
  const normalized = value
    .replace(/\s*-\s+/g, "\n- ")
    .replace(/\s*•\s+/g, "\n- ")
    .replace(/\n{2,}/g, "\n");

  const lines = normalized
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const bulletLike = lines.filter((l) => /^[-•*]/.test(l));
  if (bulletLike.length >= 2) {
    return bulletLike.map((l) => l.replace(/^[-•*]\s*/, "").trim());
  }
  // If we have multiple newline-separated lines, treat them as bullets as-is
  if (lines.length >= 2 && value.includes("\n")) {
    return lines;
  }
  // Fallback: split sentences conservatively
  const sentences = value
    .split(/(?<=[.!?])\s+(?=[A-Z])|\n/g)
    .map((s) => s.trim())
    .filter(Boolean);
  return sentences.length ? sentences : [value];
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const inlineMarkdownToHtml = (value) => {
  let html = escapeHtml(value);
  // bold first to avoid interfering with italic rule
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // italics (remaining single asterisks)
  html = html.replace(/\*(?!\s)([^*]+?)\*/g, "<em>$1</em>");
  // inline code
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
  return html;
};

const AIAnalysisOnDist = ({ insight, by }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const bullets = useMemo(() => parseInsightToBullets(insight), [insight]);
  const visibleBullets = expanded ? bullets : bullets.slice(0, 3);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(Array.isArray(insight) ? insight.join("\n") : String(insight || ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // no-op
    }
  };

  if (!insight) return null;

  const title = by
    ? `AI insight on meeting distribution by ${by}`
    : "AI insight on meeting distribution";

  return (
    <Card
      withBorder
      radius="md"
      p="md"
      sx={(theme) => ({
        position: "relative",
        overflow: "hidden",
        background:
          theme.colorScheme === "dark"
            ? theme.fn.rgba(theme.colors.dark[7], 0.6)
            : theme.white,
        borderColor:
          theme.colorScheme === "dark" ? theme.colors.dark[5] : theme.colors.gray[3],
      })}
      mb={"xl"}
    >
      {/* Decorative gradient left strip */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: "linear-gradient(180deg, #39a8f0 0%, #fab005 100%)",
        }}
      />

      {/* Subtle glow in background */}
      <Box
        sx={{
          position: "absolute",
          right: -40,
          top: -40,
          width: 160,
          height: 160,
          filter: "blur(70px)",
          background: "radial-gradient(circle at center, rgba(57,168,240,0.25), rgba(250,176,5,0.15))",
          pointerEvents: "none",
        }}
      />

      <Group position="apart" align="center">
        <Group spacing="xs">
          <ThemeIcon size="md" radius="md" variant="gradient" gradient={{ from: "blue", to: "yellow" }}>
            <IconRobot size={16} />
          </ThemeIcon>
          <Text weight={600}>{title}</Text>
        </Group>

        <Group spacing="xs">
          <Tooltip label={copied ? "Copied" : "Copy insight"} withArrow>
            <ActionIcon variant="subtle" color={copied ? "teal" : "gray"} onClick={handleCopy}>
              {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            </ActionIcon>
          </Tooltip>
          <Tooltip label={expanded ? "Show less" : "Show more"} withArrow>
            <ActionIcon
              variant="light"
              color="blue"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Divider my="sm" variant="dashed" />

      <Stack spacing={6}>
        {visibleBullets.map((item, idx) => (
          <Group key={`${idx}-${item.slice(0, 10)}`} align="flex-start" spacing="xs" noWrap>
            <ThemeIcon size={10} color="blue" radius="xl" variant="light" mt={6}>
              <Box sx={{ width: 4, height: 4, borderRadius: 999, background: "currentColor" }} />
            </ThemeIcon>
            <Text
              size="sm"
              sx={{ lineHeight: 1.55 }}
              dangerouslySetInnerHTML={{ __html: inlineMarkdownToHtml(item) }}
            />
          </Group>
        ))}
        {bullets.length > visibleBullets.length && (
          <Text size="xs" color="dimmed" mt={2}>
            {bullets.length - visibleBullets.length} more point(s) hidden
          </Text>
        )}
      </Stack>

      <Text size="xs" color="dimmed" mt="md">
        Generated by Gaintime AI based on your organization’s meeting data
      </Text>
    </Card>
  );
};

export default AIAnalysisOnDist;
